import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface LeaderboardRow {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  xp_gained: number;
  lessons_completed: number;
  challenge_wins: number;
  is_self: boolean;
  is_household: boolean;
}

export interface WeeklyRecap {
  lessons_completed: number;
  xp_gained: number;
  challenge_wins: number;
  challenge_losses: number;
  current_streak: number;
  top_topic: string | null;
}

export interface FamilyStanding {
  user_id: string;
  display_name: string | null;
  role: string;
  xp_week: number;
  lessons_week: number;
  challenge_wins_week: number;
  current_streak: number;
  total_xp: number;
}

export function useFriendLeaderboard(windowDays = 7) {
  const { user } = useAuth();
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase.rpc("get_friend_leaderboard", { p_window_days: windowDays });
    setRows((data) || []);
    setLoading(false);
  }, [user, windowDays]);

  useEffect(() => { refetch(); }, [refetch]);
  return { rows, loading, refetch };
}

export function useWeeklyRecap() {
  const { user } = useAuth();
  const [recap, setRecap] = useState<WeeklyRecap | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    supabase.rpc("get_weekly_recap").then(({ data }) => {
      setRecap((data as unknown as WeeklyRecap) || null);
      setLoading(false);
    });
  }, [user]);

  return { recap, loading };
}

export function useFamilyStandings() {
  const { user } = useAuth();
  const [standings, setStandings] = useState<FamilyStanding[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    supabase.rpc("get_family_standings").then(({ data }) => {
      setStandings((data) || []);
      setLoading(false);
    });
  }, [user]);

  return { standings, loading };
}
